/* TagsPanel — admin tag clean-up: merge spelling variants, rename, delete, manage remembered rules. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import CuisineAdmin from "@/pages/admin/CuisineAdmin";
import { MagnifyingGlass, GitMerge, PencilSimple, Trash, X } from "@phosphor-icons/react";

function GroupRow({ g, onMerge, onRename, onDelete, busy }) {
  const [target, setTarget] = useState(g.suggested);
  const [renaming, setRenaming] = useState(null);
  const dup = g.variants.length > 1;
  return (
    <li className="card-recipe !p-4" data-testid={`tag-group-${g.sig}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {g.variants.map((v) => (
          <span key={v.tag} className="inline-flex items-center gap-1 rounded-full bg-rf-cream-alt px-2.5 py-1 text-xs font-bold text-rf-ink" data-testid={`tag-variant-${g.sig}-${v.tag}`}>
            {v.tag} <span className="text-rf-ink-soft font-semibold">({v.count})</span>
            <button type="button" title="Rename" aria-label={`Rename ${v.tag}`} onClick={() => setRenaming({ from: v.tag, to: v.tag })} data-testid={`tag-rename-open-${v.tag}`} className="ml-1 text-rf-ink-soft hover:text-rf-ink"><PencilSimple size={12} /></button>
            <button type="button" title="Delete" aria-label={`Delete ${v.tag}`} disabled={busy} onClick={() => onDelete(v.tag, v.count)} data-testid={`tag-delete-${v.tag}`} className="text-rf-ink-soft hover:text-red-700"><Trash size={12} /></button>
          </span>
        ))}
        <span className="text-xs text-rf-ink-soft ml-auto">{g.total} recipe{g.total === 1 ? "" : "s"}</span>
      </div>
      {dup && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-bold text-rf-ink-soft" htmlFor={`tag-target-${g.sig}`}>Final spelling</label>
          <input id={`tag-target-${g.sig}`} list={`tag-opts-${g.sig}`} value={target} onChange={(e) => setTarget(e.target.value)} data-testid={`tag-target-${g.sig}`}
            className="rounded-lg border border-rf-ink/20 px-2 py-1 text-sm w-48" />
          <datalist id={`tag-opts-${g.sig}`}>{g.variants.map((v) => <option key={v.tag} value={v.tag} />)}</datalist>
          <button type="button" disabled={busy || !target.trim()} onClick={() => onMerge(g.variants.map((v) => v.tag), target.trim())} data-testid={`tag-merge-${g.sig}`}
            className="btn-amber !py-1 !px-3 text-sm disabled:opacity-50"><GitMerge size={14} /> Merge</button>
        </div>
      )}
      {renaming && (
        <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`tag-rename-form-${renaming.from}`}>
          <span className="text-xs font-bold text-rf-ink-soft">Rename &ldquo;{renaming.from}&rdquo; to</span>
          <input value={renaming.to} onChange={(e) => setRenaming({ ...renaming, to: e.target.value })} data-testid="tag-rename-input"
            className="rounded-lg border border-rf-ink/20 px-2 py-1 text-sm w-48" />
          <button type="button" disabled={busy || !renaming.to.trim() || renaming.to.trim() === renaming.from} onClick={() => { onRename(renaming.from, renaming.to.trim()); setRenaming(null); }} data-testid="tag-rename-save" className="btn-amber !py-1 !px-3 text-sm disabled:opacity-50">Rename</button>
          <button type="button" onClick={() => setRenaming(null)} className="text-xs underline text-rf-ink-soft">Cancel</button>
        </div>
      )}
    </li>
  );
}

function IntroRow({ row, onSaved }) {
  const [text, setText] = useState(row.intro || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/tags/intros/${row.sig}`, { intro: text, tag: row.tag }); toast.success(`Saved intro for “${row.tag}”`); onSaved(); }
    catch { toast.error("Couldn't save the intro"); } finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/tags/intros/${row.sig}/regenerate`, { tag: row.tag }); setText(data.intro); toast.success("New intro written"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't regenerate"); } finally { setBusy(false); }
  };
  return (
    <li className="card-recipe !p-4" data-testid={`tag-intro-row-${row.sig}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <a href={`/recipes/tag/${row.slug}`} target="_blank" rel="noreferrer" className="font-bold text-rf-ink hover:text-rf-orange">{row.tag}</a>
        <span className="text-xs text-rf-ink-soft">{row.count} recipes{row.source ? ` · ${row.source === "admin" ? "edited by admin" : "AI-written"}` : " · waiting for AI"}</span>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid={`tag-intro-text-${row.sig}`}
        placeholder="No intro yet — the AI writes one within 10 minutes, or write your own."
        className="w-full rounded-lg border border-rf-ink/20 px-3 py-2 text-sm" />
      {(row.faqs || []).length > 0 && (
        <details className="mt-2 text-xs text-rf-ink-soft" data-testid={`tag-intro-faqs-${row.sig}`}>
          <summary className="cursor-pointer font-bold">{row.faqs.length} FAQs on this page</summary>
          <ul className="mt-1 space-y-1">{row.faqs.map((f, i) => <li key={i}><b className="text-rf-ink">{f.q}</b> — {f.a}</li>)}</ul>
        </details>
      )}
      <div className="flex gap-2 mt-2">
        <button type="button" disabled={busy || text.split(/\s+/).join(" ").trim() === (row.intro || "").trim()} onClick={save} data-testid={`tag-intro-save-${row.sig}`} className="btn-amber !py-1 !px-3 text-sm disabled:opacity-50">Save</button>
        <button type="button" disabled={busy} onClick={regen} data-testid={`tag-intro-regen-${row.sig}`} className="rounded-full border border-rf-ink/20 px-3 py-1 text-sm font-bold hover:bg-rf-cream-alt disabled:opacity-50">
          {busy ? "Working…" : "Regenerate intro + FAQs"}
        </button>
      </div>
    </li>
  );
}

function TagIntros() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/tags/intros").then(({ data }) => setRows(data.intros || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="mt-12" data-testid="tag-intros-section">
      <h3 className="font-display text-xl font-black text-rf-ink mb-1">Tag page intros</h3>
      <p className="text-sm text-rf-ink-soft mb-4">Tags with 2+ public recipes get a short AI-written intro on their page. Edit any of them, or regenerate.</p>
      {rows === null ? <p className="text-sm text-rf-ink-soft">Loading…</p> : rows.length ? (
        <ul className="grid gap-3">{rows.map((r) => <IntroRow key={r.sig} row={r} onSaved={load} />)}</ul>
      ) : <p className="text-sm text-rf-ink-soft">No tags have 2+ public recipes yet.</p>}
    </section>
  );
}

export default function TagsPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [onlyDup, setOnlyDup] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/tags").then(({ data: d }) => setData(d)).catch(() => toast.error("Couldn't load tags")), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data: d } = await fn(); toast.success(ok(d)); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  const merge = (variants, target) => run(() => api.post("/admin/tags/merge", { variants, target }), (d) => `Merged into “${d.target}” · ${d.updated} recipe(s) updated`);
  const rename = (from, to) => run(() => api.post("/admin/tags/rename", { from, to }), (d) => `Renamed to “${d.target}” · ${d.updated} recipe(s) updated`);
  const del = (tag, count) => {
    if (!window.confirm(`Remove the tag “${tag}” from ${count} recipe(s)? It will also be dropped from future saves.`)) return;
    run(() => api.post("/admin/tags/delete", { tag }), (d) => `Deleted · ${d.updated} recipe(s) updated`);
  };
  const mergeAll = () => {
    if (!window.confirm(`Merge all ${data.duplicates} duplicate group(s) into their suggested spelling?`)) return;
    run(() => api.post("/admin/tags/merge-all"), (d) => `Merged ${d.groups} group(s) · ${d.updated} recipe(s) updated`);
  };
  const dropRule = (from) => run(() => api.delete(`/admin/tags/rules/${encodeURIComponent(from)}`), () => "Rule removed");

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.groups || []).filter((g) => (!onlyDup || g.variants.length > 1 || needle) && (!needle || g.variants.some((v) => v.tag.toLowerCase().includes(needle))));
  }, [data, q, onlyDup]);

  if (!data) return <p className="text-rf-ink-soft" data-testid="tags-loading">Loading tags…</p>;

  return (
    <div data-testid="admin-tags-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl font-black text-rf-ink">Tag clean-up</h2>
          <p className="text-sm text-rf-ink-soft max-w-2xl mt-1">
            Spellings that mean the same thing (&ldquo;Drinks&rdquo;, &ldquo;drink&rdquo;, &ldquo;DRINK&rdquo;) are grouped together. Merging updates every recipe — public and private — and is remembered, so future saves use the final spelling too.
          </p>
        </div>
        <button type="button" disabled={busy || !data.duplicates} onClick={mergeAll} data-testid="tags-merge-all" className="btn-amber disabled:opacity-50">
          <GitMerge size={16} /> Merge all suggested ({data.duplicates})
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a tag…" data-testid="tags-search" className="rounded-full border border-rf-ink/20 pl-8 pr-3 py-1.5 text-sm w-64" />
        </div>
        <label className="text-sm font-bold text-rf-ink-soft inline-flex items-center gap-2">
          <input type="checkbox" checked={onlyDup} onChange={(e) => setOnlyDup(e.target.checked)} data-testid="tags-only-duplicates" /> Only show duplicates
        </label>
        <span className="text-xs text-rf-ink-soft">{data.groups.length} tags in total</span>
      </div>
      {groups.length ? (
        <ul className="grid gap-3 mb-10" data-testid="tags-groups">
          {groups.map((g) => <GroupRow key={g.sig} g={g} onMerge={merge} onRename={rename} onDelete={del} busy={busy} />)}
        </ul>
      ) : (
        <p className="rounded-2xl bg-rf-cream-alt p-6 text-sm text-rf-ink-soft mb-10" data-testid="tags-empty">
          {onlyDup ? "No duplicate spellings — your tags are tidy. Untick “Only show duplicates” to rename or delete tags." : "No tags match."}
        </p>
      )}
      <h3 className="font-display text-xl font-black text-rf-ink mb-2">Remembered rules ({data.rules.length})</h3>
      {data.rules.length ? (
        <ul className="grid gap-1.5" data-testid="tags-rules">
          {data.rules.map((r) => (
            <li key={r.from} className="flex items-center gap-2 text-sm" data-testid={`tag-rule-${r.from}`}>
              <code className="bg-rf-cream-alt rounded px-1.5">{r.from}</code> → {r.to ? <b>{r.to}</b> : <span className="text-red-700 font-bold">removed</span>}
              <button type="button" onClick={() => dropRule(r.from)} aria-label={`Forget rule for ${r.from}`} data-testid={`tag-rule-delete-${r.from}`} className="text-rf-ink-soft hover:text-red-700"><X size={12} /></button>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-rf-ink-soft">No rules yet — they appear here after you merge, rename or delete.</p>}
      <TagIntros />
      <CuisineAdmin />
    </div>
  );
}
